import { useChatMessages } from '@ai-sdk-tools/store';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useMemo } from 'react';

import type { UIMessageWithMetadata } from '../../../../lib/types';
import { getContextUsageSummary } from '../../lib/context-usage';

interface ChatContextUsageProps {
  className?: string;
  messageId?: string;
}

const INDICATOR_SIZE = 18;
const INDICATOR_CENTER = INDICATOR_SIZE / 2;
const INDICATOR_STROKE_WIDTH = 2;
const INDICATOR_RADIUS = 7.5;
const INDICATOR_CIRCUMFERENCE = 2 * Math.PI * INDICATOR_RADIUS;

export function ChatContextUsage({ className, messageId }: ChatContextUsageProps) {
  const isEnabled = useFeatureFlag('nova-context-usage-indicator', false);
  const messages = useChatMessages<UIMessageWithMetadata>();

  const summary = useMemo(() => {
    return getContextUsageSummary(messages);
  }, [messages]);

  if (!isEnabled) {
    return null;
  }

  if (messages.length === 0 || !summary) {
    return null;
  }

  if (messageId && summary.messageId !== messageId) {
    return null;
  }

  const indicatorOffset = INDICATOR_CIRCUMFERENCE * (1 - summary.progressPercentage / 100);
  const visibleLabel = `${summary.progressPercentage}% of chat limit`;

  return (
    <span
      className={cn(
        'text-steel-400 inline-flex h-8 shrink-0 items-center gap-2 pl-1 font-sans text-[12px] leading-4 font-normal tracking-normal whitespace-nowrap tabular-nums select-none',
        className,
      )}
      data-testid="chat-context-usage"
      role="progressbar"
      aria-label="Conversation context usage"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={summary.progressPercentage}
      aria-valuetext={visibleLabel}
    >
      <svg
        aria-hidden="true"
        className="h-[18px] w-[18px] shrink-0"
        viewBox={`0 0 ${INDICATOR_SIZE} ${INDICATOR_SIZE}`}
      >
        <circle
          className="text-steel-200"
          cx={INDICATOR_CENTER}
          cy={INDICATOR_CENTER}
          fill="none"
          r={INDICATOR_RADIUS}
          stroke="currentColor"
          strokeWidth={INDICATOR_STROKE_WIDTH}
        />
        <circle
          className="text-steel-400 transition-[stroke-dashoffset] duration-200"
          cx={INDICATOR_CENTER}
          cy={INDICATOR_CENTER}
          fill="none"
          r={INDICATOR_RADIUS}
          stroke="currentColor"
          strokeDasharray={INDICATOR_CIRCUMFERENCE}
          strokeDashoffset={indicatorOffset}
          strokeLinecap="round"
          strokeWidth={INDICATOR_STROKE_WIDTH}
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transformBox: 'fill-box' }}
        />
      </svg>
      <span>{visibleLabel}</span>
    </span>
  );
}
